import { useMemo } from 'react';
import {
  BET_PILL_HEIGHT,
  BET_PILL_WIDTH,
  BOARD_MIN_SCALE_FACTOR,
  BOARD_PORTRAIT_RATIO,
  DEALER_PORTRAIT_HEIGHT,
  DEALER_SEAT_TOP_PX,
  DEALER_SEAT_OFFSET_PX,
  FLANK_BET_GAP_PX,
  SEAT_BOTTOM_CLEARANCE_ARC_FRACTION,
  SEAT_CONTAINER_HEIGHT,
  SEAT_CARD_WIDTH,
  SMALL_CARD_HEIGHT,
  SEAT_TOP_CLEARANCE_ARC_FRACTION,
  TABLE_DOWN_SHIFT_PCT
} from 'constants/gameConstants';

import { seatActionBubbleFloor } from 'components/Seat/components/SeatActionBubble';
import { PORTRAIT_BOTTOM_SEAT_ANCHOR_Y } from 'components/Seat/modes/getPortraitModeStyles';
import { seatActionBubbleHeight } from 'components/Seat/styles';

import { ContainerSize } from './useContainerSize';

/** Un-shifted vertical centre of the seat ring, before TABLE_DOWN_SHIFT_PCT. */
const BASE_CENTER_Y_PORTRAIT = 46;
const BASE_CENTER_Y_LANDSCAPE = 50;

/**
 * EN-10520's SEAT RING + TABLE POSITION rework ships to MOBILE PORTRAIT ONLY.
 *
 * Two things are gated on this and nothing else: the clearance wedges that
 * decide who sits where (`seatArcFraction`), and the downward table shift
 * (`TABLE_DOWN_SHIFT_PCT`) that drops the ring, the felt oval, the apron, the
 * board column and the winner banner together.
 *
 * Everything else from that pass — seat card width, text fit, card anchoring,
 * showdown fit, dealer marker, edge clamp, bet placement, layering, winner
 * animation, board placeholders, felt typography — is unconditional and ships
 * on every viewport.
 *
 * Off, the ring reverts to plain even spacing around the ellipse with no shift:
 * `seatArcFraction` with both wedges at 0 collapses to `slot / count`, which is
 * the original distribution exactly.
 */
export type SeatRingLayout = {
  /** Reserve the top / bottom clearance wedges. */
  wedges: boolean;
  /** Drop the whole table by TABLE_DOWN_SHIFT_PCT. */
  shift: boolean;
};

/** Mobile portrait gets the rework; every other viewport keeps the original. */
export const seatRingLayoutFor = (isMobile: boolean, isPortrait: boolean): SeatRingLayout => {
  const reworked = isMobile && isPortrait;
  return { wedges: reworked, shift: reworked };
};

/** The original ring: even spacing, no table drop. */
export const CLASSIC_SEAT_RING: SeatRingLayout = { wedges: false, shift: false };

/** How far the table drops for a given ring layout. */
const shiftPctFor = (layout: SeatRingLayout): number =>
  layout.shift ? TABLE_DOWN_SHIFT_PCT : 0;

/**
 * Vertical centre the whole table composes around, including EN-10520's
 * downward shift where it applies. Bets, the dealer button and the board column
 * all aim at this so they travel with the felt instead of drifting off it.
 */
export const tableCenterY = (
  isPortrait: boolean,
  layout: SeatRingLayout = CLASSIC_SEAT_RING
): number =>
  (isPortrait ? BASE_CENTER_Y_PORTRAIT : BASE_CENTER_Y_LANDSCAPE) + shiftPctFor(layout);

/**
 * Where a seat slot sits along the ring, as a fraction of total arc length.
 *
 * Slot 0 is the bottom-centre POV seat at fraction 0. The remaining `count - 1`
 * seats are spread at EVEN arc spacing over the ring minus TWO wedges — one of
 * `topGapFraction` centred on top-centre (fraction 0.5), one of
 * `bottomGapFraction` centred on the POV seat itself — so:
 *
 *   - no seat can ever land at top-centre;
 *   - the top-most seat on each side is pushed toward its corner;
 *   - the POV's two neighbours ride up the flanks, clear of the POV's own hole
 *     cards and dealer marker (which reach much further across the felt than an
 *     opponent's do);
 *   - spacing stays uniform everywhere else, at any seat count.
 *
 * With an EVEN `count` the non-POV seats are odd in number and cannot split
 * evenly across the two sides; the extra one falls on the left (slot order
 * continues clockwise from the bottom), which is still preferable to seating
 * someone at top-centre.
 *
 * Pure and exported so the layout is unit-testable without rendering a table.
 */
export const seatArcFraction = (
  slot: number,
  count: number,
  topGapFraction: number = SEAT_TOP_CLEARANCE_ARC_FRACTION,
  bottomGapFraction: number = SEAT_BOTTOM_CLEARANCE_ARC_FRACTION
): number => {
  if (count <= 1) return 0;
  const topGap = Math.max(0, Math.min(0.9, topGapFraction));
  const bottomGap = Math.max(0, Math.min(0.9 - topGap, bottomGapFraction));
  const bottomHalf = bottomGap / 2;
  // Arc available on ONE side, between the two wedges.
  const sideArc = 0.5 - topGap / 2 - bottomHalf;

  const atSlot = (whole: number): number => {
    // Slot 0 IS the bottom-centre seat — it sits in the middle of the bottom
    // wedge rather than being pushed out of it.
    if (whole <= 0) return 0;
    if (whole >= count) return 1;
    // Walk the ring as if neither wedge existed, then step over both.
    const walked = bottomHalf + (whole / count) * 2 * sideArc;
    return walked <= bottomHalf + sideArc + 1e-9 ? walked : walked + topGap;
  };

  const wrapped = ((slot % count) + count) % count;
  const whole = Math.floor(wrapped);
  const t = wrapped - whole;
  if (t === 0) return atSlot(whole);

  // Fractional slots come from the POV seat-assignment rotation. Interpolate
  // BETWEEN the two discrete seat fractions rather than re-running the formula,
  // so the travel stays continuous across the top wedge (the seat crossing it
  // simply moves faster) instead of teleporting from one side to the other.
  const from = atSlot(whole);
  const to = whole + 1 >= count ? 1 : atSlot(whole + 1);
  return from + (to - from) * t;
};

export type SeatPosition = {
  /** CSS left as percentage (0-100) */
  leftPct: number;
  /** CSS top as percentage (0-100) */
  topPct: number;
  /** Seat is on the left half of the table */
  isLeft: boolean;
  /** Seat is horizontally centered */
  isCenter: boolean;
  /** Seat is on the right half of the table */
  isRight: boolean;
  /** Seat is on the top half of the table */
  isTop: boolean;
  /** Seat is on the bottom half of the table */
  isBottom: boolean;
  /** Angle in degrees for spotlight/label orientation */
  angle: number;
};

/**
 * Continuous sampler for the seat ring. `seatAt`/`betAt` return a position for
 * any (possibly fractional, wrapping) slot index in [0, seatCount) — slot 0 is
 * the bottom-center seat, increasing clockwise. Used to animate the POV
 * seat-assignment rotation along the ellipse arc rather than snapping between
 * discrete slots.
 */
export type SeatLayoutSampler = {
  seatCount: number;
  seatAt: (slot: number) => SeatPosition | undefined;
  betAt: (slot: number) => SeatPosition | undefined;
};

export type EllipticalPositions = {
  seatPositions: SeatPosition[];
  betPositions: SeatPosition[];
  dealerPositions: SeatPosition[];
  /** Continuous sampler matching the discrete arrays above. */
  sampler: SeatLayoutSampler;
};

/**
 * Compute seat positions around an ellipse using parametric equations.
 *
 * Seats are distributed evenly around an ellipse, starting from the
 * bottom-center (the current player's natural position) and going clockwise.
 *
 * The ellipse shape adapts based on container aspect ratio:
 * - Landscape: wider than tall (standard poker table)
 * - Portrait: taller than wide (vertical phone layout)
 */
/**
 * The seat ring on its own — positions only, no clearance, no scale.
 *
 * Exported because the ring has to be known BEFORE the painted scale is chosen:
 * how large the seats may be depends on how far apart the ring puts them (see
 * components/Table/feltScale). The positions are pure container percentages and
 * carry no dependency on `seatScale`, so there is no circularity — only the bet
 * and dealer derivations need the scale, and those stay inside the hook.
 *
 * Shared with the hook rather than reimplemented, so the scale can never be
 * computed against a different ring from the one that gets rendered.
 */
export const seatRingPositions = (
  seatCount: number,
  isPortrait: boolean,
  paddingPct: number,
  paddingPctY: number,
  ringLayout: SeatRingLayout = CLASSIC_SEAT_RING
): SeatPosition[] => {
  if (seatCount <= 0) return [];
  const geometry = buildEllipseGeometry(paddingPct, isPortrait, paddingPctY);
  return Array.from({ length: seatCount }, (_, slot) =>
    seatPositionAtSlot(geometry, seatCount, slot, ringLayout)
  );
};

export function useEllipticalPositions(
  seatCount: number,
  containerSize: ContainerSize,
  paddingPct = 8,
  betInsetPct = 12,
  // Vertical padding for the seat ellipse. Defaults to paddingPct (circular
  // behavior); pass a smaller value to push the top/bottom seats further out
  // without moving the side seats.
  paddingPctY = paddingPct,
  // The table's `--seat-scale` (see useResponsiveScale). Bets need it to size
  // their clearance off the seat furniture's REAL painted size — a fixed
  // percentage is wrong the moment the table is scaled down.
  seatScale = 1,
  // Which seat-ring layout this viewport gets. Only mobile portrait receives
  // the reworked ring + table drop — see seatRingLayoutFor.
  ringLayout: SeatRingLayout = CLASSIC_SEAT_RING,
  // How much larger the furniture is than the regime the hand-tuned percentage
  // offsets were measured in — i.e. `seatScale / containerFit`. 1 leaves every
  // tuned position bit-for-bit unchanged. See `tunedOffset`.
  tunedScaleRatio = 1
): EllipticalPositions {
  const isPortrait = containerSize.height > containerSize.width;

  return useMemo(() => {
    const emptySampler: SeatLayoutSampler = {
      seatCount: 0,
      seatAt: () => undefined,
      betAt: () => undefined
    };

    if (seatCount === 0 || containerSize.width === 0 || containerSize.height === 0) {
      return { seatPositions: [], betPositions: [], dealerPositions: [], sampler: emptySampler };
    }

    const geometry = buildEllipseGeometry(paddingPct, isPortrait, paddingPctY);
    const clearance = seatClearancePct(containerSize, seatScale);

    const seatAt = (slot: number) => seatPositionAtSlot(geometry, seatCount, slot, ringLayout);
    const betAt = (slot: number) => {
      const seat = seatAt(slot);
      return (
        seat &&
        deriveBetPosition(
          seat,
          isPortrait,
          betInsetPct,
          clearance,
          ringLayout,
          slot,
          seatCount,
          tunedScaleRatio
        )
      );
    };
    const sampler: SeatLayoutSampler = { seatCount, seatAt, betAt };

    // Discrete arrays are just the sampler evaluated at each integer slot, so
    // the animated (continuous) and settled (discrete) layouts stay in sync.
    const seatPositions = Array.from({ length: seatCount }, (_, i) =>
      seatPositionAtSlot(geometry, seatCount, i, ringLayout)
    );
    const betPositions = seatPositions.map((seat, index) =>
      deriveBetPosition(
        seat,
        isPortrait,
        betInsetPct,
        clearance,
        ringLayout,
        index,
        seatCount,
        tunedScaleRatio
      )
    );
    const dealerPositions = seatPositions.map((seat) =>
      deriveDealerPosition(seat, isPortrait, betInsetPct, ringLayout)
    );

    return { seatPositions, betPositions, dealerPositions, sampler };
  }, [
    seatCount,
    containerSize.width,
    containerSize.height,
    paddingPct,
    paddingPctY,
    betInsetPct,
    seatScale,
    ringLayout.wedges,
    ringLayout.shift,
    isPortrait,
    tunedScaleRatio
  ]);
}

/**
 * How far a bet pill has to travel from a seat's centre to clear that seat's
 * furniture, expressed as percentages of the CONTAINER — because that is the
 * unit seat/bet positions are in, while the furniture itself is sized in px and
 * scaled by `--seat-scale`.
 *
 * Deriving it rather than hard-coding percentages is what makes the placement
 * hold at 390px, at 1440px and in a shrunken 4-table grid cell alike: at a
 * scale of 1 the seat card is a small fraction of a wide table, at 0.5 in a
 * narrow cell it is a large one, and a single percentage cannot be right for
 * both.
 */
function seatClearancePct(containerSize: ContainerSize, seatScale: number) {
  const toXPct = (px: number) => (px / containerSize.width) * 100;
  const toYPct = (px: number) => (px / (containerSize.height || 1)) * 100;
  const gap = FLANK_BET_GAP_PX * seatScale;
  return {
    /**
     * Half the community board's height, as a % of the container.
     *
     * The board is px-sized and the bets are percentages, so which seats are
     * level with it depends on how tall the table is — it cannot be a constant.
     */
    // `max(1, seatScale)`, matching how HoldEmCards actually paints it: the
    // board grows with the felt but never shrinks below the size it has always
    // painted (BOARD_MIN_SCALE_FACTOR). Using a bare `seatScale` here would
    // understate the board on every shrunken grid cell — where it is floored —
    // and put bet chips that were computed to clear it straight onto it.
    boardHalfHeightPct: toYPct(
      (SMALL_CARD_HEIGHT * BOARD_CARD_SCALE * Math.max(BOARD_MIN_SCALE_FACTOR, seatScale)) / 2
    ),
    /** Half a bet pill, same reason. */
    betHalfHeightPct: toYPct((BET_PILL_HEIGHT / 2) * seatScale),
    /**
     * How far ABOVE the seat's centre its dealer marker ends.
     *
     * The marker hangs from the card's top edge (DEALER_SEAT_TOP_PX) and the card
     * is centred on the seat, so its bottom lands slightly above the centre line.
     * That is the line a chip has to sit just under.
     */
    markerBottomAbovePct: toYPct(
      (SEAT_CONTAINER_HEIGHT / 2 - (DEALER_SEAT_TOP_PX + DEALER_PORTRAIT_HEIGHT)) * seatScale
    ),
    /**
     * How far ABOVE a BOTTOM-row opponent's ring position its action bubble's
     * TOP edge paints, in portrait — the line that seat's chip has to stay over.
     *
     * Bottom-row seats are not centred on their ring position: the box is lifted
     * PORTRAIT_BOTTOM_SEAT_ANCHOR_Y of its height and then scaled about its own
     * centre, which leaves the card's top edge almost exactly on the position at
     * a phone's seat scale. The bubble then stacks off that edge: its own floor
     * (seatActionBubbleFloor — clear of the avatar AND the revealed hole cards,
     * tail included) plus its own height.
     */
    bottomRowBubbleTopAbovePct: toYPct(
      SEAT_CONTAINER_HEIGHT * (seatScale / 2 - (0.5 - PORTRAIT_BOTTOM_SEAT_ANCHOR_Y)) +
        (seatActionBubbleFloor(true) + seatActionBubbleHeight(seatScale)) * seatScale
    ),
    /**
     * Past the dealer marker's outer edge, plus half a bet pill, plus air. The
     * marker hangs OFF the card's centre-facing edge (DEALER_SEAT_OFFSET_PX is
     * negative), so clearing the card alone is not enough.
     *
     * Derived rather than a fixed percentage because the furniture is sized in
     * px and rides `--seat-scale`: at scale 1 on a 1400px table the seat card is
     * a sliver of the container, in a shrunken grid cell it is a large fraction
     * of it, and no single percentage is right for both.
     */
    dealerPlusBetX: toXPct(
      (SEAT_CARD_WIDTH / 2 - DEALER_SEAT_OFFSET_PX + BET_PILL_WIDTH / 2) * seatScale + gap
    ),

    /**
     * Past the seat CARD's centre-facing edge, plus half a bet pill, plus air.
     * Narrower than `dealerPlusBetX` because it ignores the dealer marker: it
     * backstops the portrait flank columns, where the pill is allowed to sit
     * beside the marker but never on the card's name/balance rows.
     */
    cardPlusBetX: toXPct((SEAT_CARD_WIDTH / 2 + BET_PILL_WIDTH / 2) * seatScale + gap)
  };
}

type SeatClearance = ReturnType<typeof seatClearancePct>;

/**
 * PORTRAIT flank-seat bet placement, hand-tuned on a 9-max table and measured
 * off the running felt (Jason, 2026-08-19). Applied verbatim rather than
 * re-derived, because what these encode is not "clear the furniture" — the
 * earlier derived placement already did that — but which *gap* the pill belongs
 * in once it is clear.
 *
 * `leftPct` is a fixed COLUMN of felt rather than a per-seat offset, because
 * what it encodes is which strip between the seats and the board the pill lives
 * in. It is not absolute, though: the flank seats do NOT sit at the same x at
 * every seat count (9-max puts them at 10.4% / 89.6%, 6-max at 13.0% / 87.0%),
 * so `deriveBetPosition` clamps the column inward when a seat has moved far
 * enough that the tuned value would land under its own card. `dropPct` is
 * RELATIVE to the seat, so a flank
 * seat at a different height (short-handed rings) keeps the same relationship to
 * its own player.
 *
 * The upper pair rides just above the community board's top edge and the lower
 * pair just below its bottom edge, which is why their drops differ. The drops
 * are the odd-looking numbers they are because they are back-solved from the
 * measured tops — a 9-max ring puts its upper flank seats at 39.466% and its
 * lower ones at 57.383%, landing the pills on the specified 44.5% and 61%.
 *
 * NOTE: left and right are NOT mirror images (28/28.5 vs 74/75 — a symmetric
 * right side would read 72/71.5). That asymmetry is as supplied; if it turns
 * out to be eyeballing rather than intent, mirroring is a two-number change.
 */
/**
 * Where the POV's two NEIGHBOURS sit horizontally in mobile portrait, measured
 * off the running felt (Jason, 2026-08-24) rather than taken from the ring.
 *
 * The ring's bottom curves in much harder than the table graphic does, so the
 * arc position that looks right vertically sits too far inboard horizontally.
 * These two seats take their HEIGHT from the ring like everyone else and their
 * COLUMN from here.
 *
 * NOTE the asymmetry against the viewport clamp: hole cards fan LEFT on every
 * seat, so a left-column seat needs ~55px of room outside it and a right-column
 * one only half a seat card. On a 390px phone that puts the clamp at 13.6% /
 * 89.6%, so Ivy reaches 88% exactly while Bryce is held at 13.6% — 12% would
 * leave his hole cards ~2px off the screen edge. He reaches 12% on any portrait
 * viewport wide enough for the clamp to relax. See seatEdgeInsets.
 */
const PORTRAIT_POV_NEIGHBOUR_COLUMN = { left: 12, right: 88 } as const;

/**
 * Where the POV NEIGHBOURS' bet chips sit, as an OFFSET FROM THEIR OWN SEAT.
 *
 * Their seats are pinned out at the rail (PORTRAIT_POV_NEIGHBOUR_COLUMN), and the
 * generic portrait rule then put the chip 9.4% inboard and 6% UP — into the strip
 * the seat's ACTION BADGE occupies, so STRADDLE / BET overlapped the amount
 * (reported 2026-09-02, 9-max).
 *
 * Two things are in the way and they are on opposite sides:
 *
 *   - the action badge, in the airspace ABOVE the avatar;
 *   - the dealer marker, which hangs off the card's CENTRE-FACING edge — i.e.
 *     inboard, exactly the direction the chip moves to escape the badge.
 *
 * Far enough inboard clears the marker outright, and the chip can then sit high
 * beside the card instead of being pushed down past it. 18.5% inboard and 2% up
 * is the first pair that clears the badge, the card AND the marker at every seat
 * count from 6 to 9 (17.5% still catches the marker).
 *
 * RELATIVE, not absolute. The neighbours' seats sit lower as the table fills —
 * 64.3% at 6-max, 71.5% at 9-max — so a fixed percentage would drift out of
 * position at every count but the one it was measured at. That is the mistake the
 * flank bet columns already made once.
 */
const POV_NEIGHBOUR_BET_INWARD_PCT = 18.5;
const POV_NEIGHBOUR_BET_LIFT_PCT = 2;

/**
 * How much larger the seat furniture is than the regime these tuned percentages
 * were measured in.
 *
 * Every hand-tuned offset in this file is a CONTAINER PERCENTAGE measured
 * against furniture painted at the container's own fit — when `--seat-scale` was
 * exactly `useResponsiveScale`'s output and nothing else. They are clearances in
 * disguise, so they only hold while that relationship does.
 *
 * The player's Table View Size is now folded into `--seat-scale`
 * (components/Table/index.tsx), which breaks it: the furniture grows while a
 * fixed percentage of the container does not. Measured, this bites immediately —
 * at a 1.05x multiplier on a 390x740 portrait table the POV neighbours' pills
 * are already on their own dealer markers, and by 1.15x the POV's own pill is on
 * its seat card.
 *
 * The derived clearances (`dealerPlusBetX`, `cardPlusBetX`) do not have this
 * problem — they multiply by `seatScale` already, which is why the FLANK seats
 * survive untouched: `deriveBetPosition` clamps them against a scaled value.
 * These offsets have no such clamp, so they take the ratio directly.
 *
 * 1 means "furniture is exactly the size the tuning assumed", so every existing
 * position is bit-for-bit unchanged at the default view scale — the correction
 * only ever applies above it.
 */
const tunedOffset = (pct: number, tunedScaleRatio: number): number => pct * tunedScaleRatio;

/**
 * The board's painted scale, RELATIVE to `--seat-scale`.
 *
 * Only portrait consults this (see the note on `boardHalfHeightPct`), and
 * portrait renders the board at BOARD_PORTRAIT_RATIO of the seat scale —
 * the same constant `HoldEmCards` paints with, imported rather than restated so
 * the two cannot drift.
 */
const BOARD_CARD_SCALE = BOARD_PORTRAIT_RATIO;

/**
 * Vertical centre of the community board, as a % of the table container.
 *
 * NOT `tableCenterY`: the seat ring is centred at 46% in portrait so the oval
 * reads right, while the board sits on the felt's own centre at 50%. Using the
 * ring's centre here puts the board 4% too high and every decision that depends
 * on it comes out wrong for the seats nearest it.
 */
const boardCentreY = (layout: SeatRingLayout): number => 50 + shiftPctFor(layout);

/**
 * Which side of its own dealer marker a tuned bet chip sits on.
 *
 * The chip goes ABOVE the marker when the seat is clear of the community board,
 * and BELOW it otherwise. Everything about that decision comes from the seat and
 * the board, so it re-decides itself at every seat count instead of being a value
 * per table size (reported 2026-09-03: the chips read wrong at 3, 4, 5, 6 and 7
 * seats, each in a different direction).
 *
 * "Clear of the board" means one of two things:
 *
 *   - the seat sits ABOVE the board, so lifting the chip moves it further away;
 *   - the seat sits FAR BELOW it, where there is room between the two for the
 *     chip to sit high and still belong to the player.
 *
 * A seat level with the board, or just under it, has no such room — lifting the
 * chip there puts it on the cards, so it drops instead.
 *
 * The threshold is what separates a 6-max neighbour (10% below the board, chip
 * up) from a 5-max one (4.9% below, chip down).
 */
const BET_FAR_BELOW_BOARD_PCT = 8;

/** Lift that clears the dealer marker outright, for chips in its column. */
const BET_ABOVE_MARKER_LIFT_PCT = 5;

/**
 * Air between the dealer marker's bottom edge and the chip below it, as a % of
 * the table's height.
 *
 * 0.4% (~3px) was too tight: the chip and the button read as one overlapping
 * blob at 6, 8 and 9 seats (reported 2026-09-03). 2% is the distance asked for
 * by name — close enough that the chip still belongs to the player, far enough
 * that the two shapes separate.
 */
const BET_MARKER_GAP_PCT = 2;

/**
 * The chip's resting place: immediately UNDER its own dealer marker.
 *
 * This is what "under the dealer button" means everywhere below. Anything
 * further down had to be forced there by the community board, and reads as
 * detached from the player (reported 2026-09-03 at 4, 5, 6 and 8 seats).
 */
const justUnderMarkerPct = (seatTopPct: number, clearance: SeatClearance): number =>
  seatTopPct -
  clearance.markerBottomAbovePct +
  clearance.betHalfHeightPct +
  BET_MARKER_GAP_PCT;

/** Air kept between a chip and the community board. */
const BET_BOARD_GAP_PCT = 1;

/**
 * Hold a chip off the community board, on whichever side of it the seat is.
 *
 * A seat ABOVE the board can only be pushed further up — dropping it toward the
 * board is what put the chip on the cards in the first place. A seat level with
 * or below it has to go under. Getting this backwards moves a chip the whole
 * height of the felt, which is exactly what a single `Math.max` did.
 */
const clearOfBoard = (
  topPct: number,
  seatTopPct: number,
  boardCentrePct: number,
  boardHalfPct: number,
  betHalfPct: number
): number => {
  const boardTop = boardCentrePct - boardHalfPct;
  const boardBottom = boardCentrePct + boardHalfPct;
  const margin = betHalfPct + BET_BOARD_GAP_PCT;
  return seatTopPct < boardTop
    ? Math.min(topPct, boardTop - margin)
    : Math.max(topPct, boardBottom + margin);
};

/**
 * True when the chip can sit ABOVE the seat's dealer marker without meeting the
 * board. See BET_FAR_BELOW_BOARD_PCT.
 */
const betSitsAboveMarker = (
  seatTopPct: number,
  boardCentrePct: number,
  boardHalfPct: number
): boolean =>
  seatTopPct < boardCentrePct - boardHalfPct ||
  seatTopPct > boardCentrePct + boardHalfPct + BET_FAR_BELOW_BOARD_PCT;

/** Shortest distance between two slots around a ring of `count` seats. */
const slotDistance = (a: number, b: number, count: number): number => {
  const direct = Math.abs(a - b) % count;
  return Math.min(direct, count - direct);
};

/**
 * How strongly a slot is pulled to its tuned column, and which one.
 *
 * The pull FADES OUT over one slot either side rather than switching on at whole
 * slots. Fractional slots come from the POV seat-assignment rotation, so a hard
 * override would teleport a seat sideways the moment it settled; this keeps the
 * travel continuous while still landing exactly on the tuned column at rest.
 */
const povNeighbourColumn = (
  slot: number,
  count: number
): { target: number; weight: number } | null => {
  // Below 3 seats the two neighbours are the same player, and pulling them to
  // one side would just make a heads-up table lopsided.
  if (count < 3) return null;
  const wrapped = ((slot % count) + count) % count;

  const toLeft = Math.max(0, 1 - slotDistance(wrapped, 1, count));
  if (toLeft > 0) return { target: PORTRAIT_POV_NEIGHBOUR_COLUMN.left, weight: toLeft };

  const toRight = Math.max(0, 1 - slotDistance(wrapped, count - 1, count));
  if (toRight > 0) return { target: PORTRAIT_POV_NEIGHBOUR_COLUMN.right, weight: toRight };

  return null;
};

const PORTRAIT_FLANK_BET = {
  upperLeft: { leftPct: 28, dropPct: 5.034 },
  lowerLeft: { leftPct: 28.5, dropPct: 3.617 },
  upperRight: { leftPct: 74, dropPct: 5.034 },
  lowerRight: { leftPct: 75, dropPct: 3.617 }
} as const;

/**
 * The COLUMNS above, exported for the felt-scale clamp.
 *
 * These are absolute percentages of the container — a fixed strip of felt, not
 * an offset from any seat — so as the furniture grows a seat card can reach a
 * column that was comfortably clear of it. That makes them a constraint on how
 * large the felt may be painted, which is components/Table/feltScale's business,
 * and it cannot be expressed as a seat-to-seat gap because the pill is not
 * placed relative to a seat at all.
 */
export const PORTRAIT_FLANK_BET_COLUMNS_PCT: readonly number[] = [
  PORTRAIT_FLANK_BET.upperLeft.leftPct,
  PORTRAIT_FLANK_BET.lowerLeft.leftPct,
  PORTRAIT_FLANK_BET.upperRight.leftPct,
  PORTRAIT_FLANK_BET.lowerRight.leftPct
];

// --- Bet / dealer derivations -------------------------------------------------
// Each is a pure function of a seat position so the discrete arrays and the
// continuous sampler produce identical results.

const PORTRAIT_CLEAR_X = 12;
const PORTRAIT_CLEAR_Y = 6;
const PORTRAIT_BET_GAP = 0.5;
// topPct below this is treated as a "top" seat (bigger drop). Stricter than the
// seat's own isTop (<40) so mid-height side seats at smaller tables aren't
// pushed down toward the board.
const PORTRAIT_TOP_THRESHOLD = 32;
const PORTRAIT_SIDE_DOWN = 3; // side seats — sit close, just below the player
const PORTRAIT_TOP_DOWN = 9; // top seats — below the name/stack pill
const PORTRAIT_BOTTOM_UP = 6; // bottom-row seats — up into the table

/**
 * Air between a bottom-row opponent's chip and the top of its action bubble, as
 * a % of the table's height.
 *
 * PORTRAIT_BOTTOM_UP predates the bubble. The bubble now sits in the airspace
 * above the avatar and opens toward the felt's centre — the direction the chip
 * sits in — and at a 390px table its label is floored at a readable size, so it
 * paints ~19px tall. A 6% lift put the chip squarely on it for both of the POV's
 * neighbours (EN-10781). The chip now goes at least clear of the bubble's top
 * edge, derived from the furniture so it holds at every seat scale.
 */
const PORTRAIT_BET_BUBBLE_GAP_PCT = 0.5;

/**
 * A seat is on the FLANK when the direction to the table centre is mostly
 * horizontal — the left/right columns at the felt's widest point. Below this
 * ratio the seat's bet needs the dedicated placement in `deriveBetPosition`;
 * above it, the generic toward-centre shift already reads fine.
 */
const FLANK_DIRECTION_RATIO = 0.5;

/**
 * The tuned bet for a POV neighbour, and how strongly it applies.
 *
 * Fades over one slot either side, exactly as the seat column does, so a chip
 * does not jump the instant its seat settles during the POV rotation.
 */
const povNeighbourBet = (
  seat: SeatPosition,
  slot: number | undefined,
  count: number,
  clearance: SeatClearance,
  boardCentrePct: number,
  tunedScaleRatio: number
): { leftPct: number; topPct: number; weight: number } | null => {
  // Heads-up has a single neighbour; pulling it to one side is just lopsided.
  if (slot === undefined || count < 3) return null;
  const wrapped = ((slot % count) + count) % count;
  const weight = Math.max(
    Math.max(0, 1 - slotDistance(wrapped, 1, count)),
    Math.max(0, 1 - slotDistance(wrapped, count - 1, count))
  );
  if (weight <= 0) return null;

  // Read the side off the SEAT rather than the slot, so a seat part-way through
  // the rotation moves its chip to whichever side it is actually on.
  const inward = Math.sign(50 - seat.leftPct) || 1;
  const boardHalf = clearance.boardHalfHeightPct;
  const above = betSitsAboveMarker(seat.topPct, boardCentrePct, boardHalf);
  const topPct = above
    ? seat.topPct - tunedOffset(POV_NEIGHBOUR_BET_LIFT_PCT, tunedScaleRatio)
    : // Below: just under its own marker, pushed further only if the board is
      // in the way.
      clearOfBoard(
        justUnderMarkerPct(seat.topPct, clearance),
        seat.topPct,
        boardCentrePct,
        boardHalf,
        clearance.betHalfHeightPct
      );
  return {
    leftPct: seat.leftPct + inward * tunedOffset(POV_NEIGHBOUR_BET_INWARD_PCT, tunedScaleRatio),
    topPct,
    weight
  };
};

/**
 * Bet-chip placement for a seat.
 *
 * The POV neighbours override the generic result, blended by how close the slot
 * is to them. Blending from the GENERIC bet rather than from the seat matters:
 * at weight 0 the two have to agree exactly, or the chip jumps as a rotating
 * seat crosses out of the neighbour's slot.
 */
function deriveBetPosition(
  seat: SeatPosition,
  isPortrait: boolean,
  betInsetPct: number,
  clearance: SeatClearance,
  layout: SeatRingLayout = CLASSIC_SEAT_RING,
  slot?: number,
  seatCount = 0,
  tunedScaleRatio = 1
): SeatPosition {
  const generic = deriveGenericBetPosition(
    seat,
    isPortrait,
    betInsetPct,
    clearance,
    layout,
    tunedScaleRatio
  );
  // Reworked ring only, so no other viewport moves.
  const tuned =
    isPortrait && layout.wedges
      ? povNeighbourBet(seat, slot, seatCount, clearance, boardCentreY(layout), tunedScaleRatio)
      : null;
  if (!tuned) return generic;
  return {
    ...generic,
    leftPct: generic.leftPct + (tuned.leftPct - generic.leftPct) * tuned.weight,
    topPct: generic.topPct + (tuned.topPct - generic.topPct) * tuned.weight
  };
}

/** The ordinary rule every seat but the POV's neighbours uses. */
function deriveGenericBetPosition(
  seat: SeatPosition,
  isPortrait: boolean,
  betInsetPct: number,
  clearance: SeatClearance,
  layout: SeatRingLayout = CLASSIC_SEAT_RING,
  tunedScaleRatio = 1
): SeatPosition {
  const centerX = 50;
  const centerY = tableCenterY(isPortrait, layout);
  const betLerp = betInsetPct / 100;
  const isCurrentPlayerSeat = seat.isBottom && seat.isCenter;

  // --- FLANK SEATS ----------------------------------------------------------
  // The left/right columns at the felt's widest point. Their bets used to be
  // shifted a flat 12% toward centre (portrait) / lerped 68% of the usual pull
  // (landscape), which put the pill straight through the seat card's name and
  // balance rows — and, once the dealer marker moved out to sit beside the name
  // (DEALER_SEAT_OFFSET_PX), through the marker as well. Reported 2026-08-19 on
  // Diego / Greta / Carla / Hugo in a 9-max portrait table.
  //
  // Which way they escape differs by orientation, because the space differs:
  //
  //  - LANDSCAPE has room to the side. The felt is wide, the board sits well
  //    inboard, and the seat card is narrow relative to the container — so the
  //    pill simply clears the card AND the marker horizontally and stays level
  //    with the player.
  //  - PORTRAIT does not. The gap between the flank seat and the board is only
  //    a few percent wide, and that gap is exactly where the dealer marker now
  //    lives. So the pill moves out to a fixed column of felt and drops into the
  //    band above or below the community board — see PORTRAIT_FLANK_BET, whose
  //    values are measured off the running table rather than derived.
  const dirX = centerX - seat.leftPct;
  const dirY = centerY - seat.topPct;
  const towardCentre = Math.hypot(dirX, dirY) || 1;
  const isFlankSeat =
    Math.abs(dirY / towardCentre) < FLANK_DIRECTION_RATIO * Math.abs(dirX / towardCentre);

  if (isFlankSeat) {
    const inward = Math.sign(dirX) || 1;
    if (!isPortrait) {
      return {
        ...seat,
        leftPct: seat.leftPct + inward * clearance.dealerPlusBetX,
        topPct: seat.topPct
      };
    }
    const isLeftFlank = seat.leftPct < centerX;
    const isUpperFlank = seat.topPct < centerY;
    const tuned = isUpperFlank
      ? isLeftFlank
        ? PORTRAIT_FLANK_BET.upperLeft
        : PORTRAIT_FLANK_BET.upperRight
      : isLeftFlank
      ? PORTRAIT_FLANK_BET.lowerLeft
      : PORTRAIT_FLANK_BET.lowerRight;
    // The columns were measured on a 9-max ring, whose flank seats sit at
    // 10.4% / 89.6%. FEWER seats pull them inboard — 6-max lands them at
    // 13.0% / 87.0% — and a column that cleared the card comfortably out there
    // can end up under the name/balance rows once the seat has moved 10% of the
    // felt toward it. Reported on Frank in a 6-max portrait table.
    //
    // So the tuned value is a STARTING POINT, clamped inward far enough to clear
    // this seat's own card. The clamp only bites where the column would actually
    // overlap, which is why every hand-tuned 9-max position survives unchanged.
    const clearOfCard = isLeftFlank
      ? Math.max(tuned.leftPct, seat.leftPct + clearance.cardPlusBetX)
      : Math.min(tuned.leftPct, seat.leftPct - clearance.cardPlusBetX);
    // Above or below its own dealer marker, decided the same way as the POV
    // neighbours'. A flank chip shares the marker's COLUMN, so going above has
    // to clear it outright rather than just tuck alongside.
    const boardHalf = clearance.boardHalfHeightPct;
    const boardCentre = boardCentreY(layout);
    // Reworked ring only. The classic ring keeps its original drops verbatim —
    // the same gate the wedges, the table shift and the tuned columns all use.
    const dropped = seat.topPct + tuned.dropPct;
    const justUnder = justUnderMarkerPct(seat.topPct, clearance);
    const boardTop = boardCentre - boardHalf;
    // A chip resting under the marker is on the community cards only when the
    // seat is close enough to the board for its own furniture to reach them.
    const underIsOnBoard =
      justUnder + clearance.betHalfHeightPct > boardTop &&
      justUnder - clearance.betHalfHeightPct < boardCentre + boardHalf;
    const topPct = !layout.wedges
      ? dropped
      : !underIsOnBoard
      ? justUnder
      : // The board is where the chip wants to be. Go to whichever side of it
        // the seat is already on: above for a seat above the board, under it
        // otherwise.
      seat.topPct < boardTop
      ? seat.topPct - BET_ABOVE_MARKER_LIFT_PCT
      : clearOfBoard(justUnder, seat.topPct, boardCentre, boardHalf, clearance.betHalfHeightPct);
    return {
      ...seat,
      leftPct: clearOfCard,
      topPct
    };
  }

  if (isPortrait) {
    // Horizontal: toward-center shift via box-edge projection (restores the
    // previous inward placement so side bets aren't stuck at the edge).
    const dx = centerX - seat.leftPct;
    const dy = centerY - seat.topPct;
    const dist = Math.hypot(dx, dy) || 1;
    const ux = dx / dist;
    const uy = dy / dist;
    const tExit = Math.min(
      PORTRAIT_CLEAR_X / Math.max(Math.abs(ux), 1e-6),
      PORTRAIT_CLEAR_Y / Math.max(Math.abs(uy), 1e-6)
    );
    const horizontalShift = ux * (tExit + PORTRAIT_BET_GAP);
    // Vertical: up for the bottom row; otherwise below the player. Top seats
    // need the larger drop because their toward-center horizontal shift is small
    // (so the bet stays near the avatar), while mid-height side seats get a big
    // horizontal shift and can sit closer. (The current player's bet is also
    // clamped in Bet.tsx to stay above the hole cards.)
    // Same furniture-growth correction as the landscape nudges below: these are
    // clearances measured in container percent against furniture at the
    // container's own fit, so they have to grow when the player's Table View
    // Size grows the furniture. At ratio 1 they are the original numbers.
    const bottomRowTop = seat.topPct - tunedOffset(PORTRAIT_BOTTOM_UP, tunedScaleRatio);
    return {
      ...seat,
      leftPct: seat.leftPct + horizontalShift,
      topPct: seat.isBottom
        ? // The POV shows its action inside its own card, not as a bubble.
          isCurrentPlayerSeat
          ? bottomRowTop
          : Math.min(
              bottomRowTop,
              seat.topPct -
                clearance.bottomRowBubbleTopAbovePct -
                clearance.betHalfHeightPct -
                PORTRAIT_BET_BUBBLE_GAP_PCT
            )
        : seat.topPct +
          tunedOffset(
            seat.topPct < PORTRAIT_TOP_THRESHOLD ? PORTRAIT_TOP_DOWN : PORTRAIT_SIDE_DOWN,
            tunedScaleRatio
          )
    };
  }

  // Landscape: lerp toward center, plus a vertical nudge to clear the larger
  // popped avatars. Top seats need a bigger push down (their bet sits just under
  // a full-height avatar/name pill); current player most.
  // These nudges are clearances too — the POV's -10 lifts its pill off its own
  // seat card — so they take the same furniture-growth ratio as the tuned
  // neighbour offsets. At ratio 1 they are the original numbers exactly.
  const yNudge = tunedOffset(
    seat.isTop ? 6 : isCurrentPlayerSeat ? -10 : seat.isBottom ? -5 : 0,
    tunedScaleRatio
  );
  // Mid-height left/right seats hug the rail — reduce their horizontal pull so
  // the bet sits close to the player (but still clears the avatar) instead of
  // drifting toward center. Most such seats are caught by the flank branch
  // above and never reach here; this remains the fallback for the in-between
  // ones (short-handed tables, where a "side" seat can still point diagonally
  // at the centre).
  const isSideSeat = !seat.isTop && !seat.isBottom && !seat.isCenter;
  const lerp = isSideSeat ? betLerp * 0.68 : betLerp;
  return {
    ...seat,
    leftPct: seat.leftPct + (centerX - seat.leftPct) * lerp,
    topPct: seat.topPct + (centerY - seat.topPct) * lerp + yNudge
  };
}

/** Dealer-button resting placement: slightly further toward center than bets. */
function deriveDealerPosition(
  seat: SeatPosition,
  isPortrait: boolean,
  betInsetPct: number,
  layout: SeatRingLayout = CLASSIC_SEAT_RING
): SeatPosition {
  const centerX = 50;
  const centerY = tableCenterY(isPortrait, layout);
  const dealerLerp = (betInsetPct + 3) / 100;
  return {
    ...seat,
    leftPct: seat.leftPct + (centerX - seat.leftPct) * dealerLerp,
    topPct: seat.topPct + (centerY - seat.topPct) * dealerLerp
  };
}

// --- Ellipse geometry ---------------------------------------------------------

type EllipseGeometry = {
  centerX: number;
  centerY: number;
  radiusX: number;
  radiusY: number;
  n: number;
  arcLengths: number[];
  sampleAngles: number[];
  totalLength: number;
};

/** Get x,y on the superellipse at a given angle */
function superellipsePoint(angle: number, radiusX: number, radiusY: number, n: number) {
  const cosA = Math.cos(angle);
  const sinA = Math.sin(angle);
  const absCos = Math.abs(cosA);
  const absSin = Math.abs(sinA);

  const r = Math.pow(Math.pow(absCos / radiusX, n) + Math.pow(absSin / radiusY, n), -1 / n);

  return { x: r * cosA, y: r * sinA };
}

/**
 * Build the arc-length lookup table for the ellipse once, so any slot (discrete
 * or fractional) can be resolved to an evenly-spaced point cheaply.
 */
function buildEllipseGeometry(
  paddingPct: number,
  isPortrait: boolean,
  paddingPctY: number = paddingPct
): EllipseGeometry {
  const centerX = 50;
  // BASE centre — TABLE_DOWN_SHIFT_PCT is added at the very end of
  // seatPositionAtSlot, AFTER the isTop/isBottom flags are derived, so shifting
  // the table down can't silently reclassify seats for every consumer of those
  // flags (bet placement, seat anchoring, card sort order).
  const centerY = isPortrait ? BASE_CENTER_Y_PORTRAIT : BASE_CENTER_Y_LANDSCAPE;

  const radiusX = isPortrait ? 46 - paddingPct : 52 - paddingPct;
  const radiusY = isPortrait ? 40 - paddingPctY : 40 - paddingPctY;

  // Superellipse: >2 = more rectangular. Landscape uses mild superellipse to
  // match CSS border-radius pill shape and prevent corner bunching.
  const n = isPortrait ? 2.3 : 2.2;

  // Sample many points around the curve to build an arc-length lookup table.
  const SAMPLES = 1000;
  const startAngle = Math.PI / 2; // bottom center
  const arcLengths: number[] = [0];
  const sampleAngles: number[] = [startAngle];

  let prevPt = superellipsePoint(startAngle, radiusX, radiusY, n);

  for (let i = 1; i <= SAMPLES; i++) {
    const angle = startAngle + (2 * Math.PI * i) / SAMPLES;
    const pt = superellipsePoint(angle, radiusX, radiusY, n);
    const dx = pt.x - prevPt.x;
    const dy = pt.y - prevPt.y;
    arcLengths.push(arcLengths[i - 1] + Math.sqrt(dx * dx + dy * dy));
    sampleAngles.push(angle);
    prevPt = pt;
  }

  return {
    centerX,
    centerY,
    radiusX,
    radiusY,
    n,
    arcLengths,
    sampleAngles,
    totalLength: arcLengths[SAMPLES]
  };
}

/**
 * Resolve a (possibly fractional, wrapping) slot index to a seat position. Slot
 * 0 is the bottom-center seat; the value increases clockwise and wraps modulo
 * `count`, so `seatPositionAtSlot(geo, count, i)` for integer `i` reproduces the
 * old discrete layout exactly.
 */
function seatPositionAtSlot(
  geo: EllipseGeometry,
  count: number,
  slot: number,
  layout: SeatRingLayout = CLASSIC_SEAT_RING
): SeatPosition {
  const { centerX, centerY, radiusX, radiusY, n, arcLengths, sampleAngles, totalLength } = geo;
  const SAMPLES = arcLengths.length - 1;

  // seatArcFraction wraps the slot into [0, count) itself and keeps the top
  // wedge clear — see its docblock for the distribution rule. With the wedges
  // off, both gaps are 0 and it collapses to plain `slot / count`: the original
  // even distribution, bit for bit.
  const topGap = layout.wedges ? SEAT_TOP_CLEARANCE_ARC_FRACTION : 0;
  const bottomGap = layout.wedges ? SEAT_BOTTOM_CLEARANCE_ARC_FRACTION : 0;
  const targetLen = seatArcFraction(slot, count, topGap, bottomGap) * totalLength;

  // Binary search for the sample index.
  let lo = 0;
  let hi = SAMPLES;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (arcLengths[mid] < targetLen) lo = mid + 1;
    else hi = mid;
  }

  // Interpolate between samples for precision.
  const segStart = Math.max(lo - 1, 0);
  const segEnd = lo;
  const segLen = arcLengths[segEnd] - arcLengths[segStart];
  const t = segLen > 0 ? (targetLen - arcLengths[segStart]) / segLen : 0;
  const angle = sampleAngles[segStart] + t * (sampleAngles[segEnd] - sampleAngles[segStart]);

  const pt = superellipsePoint(angle, radiusX, radiusY, n);
  const ringLeftPct = centerX + pt.x;
  // The POV's neighbours take their column from the tuned values instead of
  // the ring — reworked ring only, so no other viewport moves.
  const tunedColumn = layout.wedges ? povNeighbourColumn(slot, count) : null;
  const leftPct = tunedColumn
    ? ringLeftPct + (tunedColumn.target - ringLeftPct) * tunedColumn.weight
    : ringLeftPct;
  // Flags are read off the UN-shifted position so the whole-table drop stays a
  // pure translation — see buildEllipseGeometry.
  const baseTopPct = centerY + pt.y;

  const isLeft = leftPct < 40;
  const isRight = leftPct > 60;
  const isCenter = !isLeft && !isRight;
  const isTop = baseTopPct < 40;
  const isBottom = baseTopPct > 60;

  return {
    leftPct,
    topPct: baseTopPct + shiftPctFor(layout),
    isLeft,
    isCenter,
    isRight,
    isTop,
    isBottom,
    angle: (angle * 180) / Math.PI
  };
}
